import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

export default function NewYorkToIreland() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="pt-24 pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold text-[#0A381F] mb-6">
              Moving to Ireland from New York: Complete 2024 Guide
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto mb-8">
              Everything New Yorkers need to know about relocating from NYC to Ireland. Compare costs, find jobs, and plan your move from the Big Apple to the Emerald Isle.
            </p>
            <img 
              src="https://public.readdy.ai/ai/img_res/c1618252aa38efd5571a61256fb61520.jpg"
              alt="Moving from New York to Ireland comparison"
              className="w-full h-96 object-cover rounded-lg shadow-lg"
            />
          </div>

          {/* Quick Stats Comparison */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <div className="bg-[#0A381F] text-white p-8 rounded-lg text-center">
              <i className="ri-home-line text-5xl text-[#D4AF37] mb-4"></i>
              <h3 className="text-2xl font-bold mb-2">Housing Costs</h3>
              <p className="text-3xl font-bold text-[#D4AF37] mb-2">30-50% Less</p>
              <p className="text-sm opacity-90">Dublin vs Manhattan</p>
            </div>
            <div className="bg-[#0A381F] text-white p-8 rounded-lg text-center">
              <i className="ri-heart-pulse-line text-5xl text-[#D4AF37] mb-4"></i>
              <h3 className="text-2xl font-bold mb-2">Healthcare</h3>
              <p className="text-3xl font-bold text-[#D4AF37] mb-2">70% Cheaper</p>
              <p className="text-sm opacity-90">Than NYC costs</p>
            </div>
            <div className="bg-[#0A381F] text-white p-8 rounded-lg text-center">
              <i className="ri-flight-takeoff-line text-5xl text-[#D4AF37] mb-4"></i>
              <h3 className="text-2xl font-bold mb-2">Flight Time</h3>
              <p className="text-3xl font-bold text-[#D4AF37] mb-2">6-7 Hours</p>
              <p className="text-sm opacity-90">Direct from JFK/EWR</p>
            </div>
          </div>

          {/* Why New Yorkers Love Ireland */}
          <section className="mb-16">
            <h2 className="text-4xl font-bold text-[#0A381F] mb-8">Why New Yorkers Are Moving to Ireland</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-2xl font-bold text-[#0A381F] mb-4">
                  <i className="ri-money-dollar-circle-line text-[#D4AF37] mr-2"></i>
                  Lower Cost of Living
                </h3>
                <p className="text-gray-700 mb-4">
                  New Yorkers are shocked by how much further their money goes in Ireland. A 2-bedroom apartment in Dublin costs $2,400-3,500/month compared to $4,000-7,000 in Manhattan. Even with similar salaries, you'll save significantly more living in Ireland than NYC.
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-check-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span>Rent is 40-60% cheaper than Manhattan</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span>Healthcare costs 70% less than NYC</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span>No state income tax (unlike NY's 6.85%)</span>
                  </li>
                </ul>
              </div>
              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-2xl font-bold text-[#0A381F] mb-4">
                  <i className="ri-briefcase-line text-[#D4AF37] mr-2"></i>
                  Tech & Finance Jobs
                </h3>
                <p className="text-gray-700 mb-4">
                  Ireland is Europe's tech hub with major companies that New Yorkers know well: Google, Facebook, Apple, Amazon, Microsoft, and top financial firms. Many NYC professionals transfer to Irish offices or find similar roles with competitive salaries.
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-check-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span>Same companies as NYC (Google, Meta, Apple)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span>Strong finance sector (Bank of America, Citi)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span>English-speaking work environment</span>
                  </li>
                </ul>
              </div>
              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-2xl font-bold text-[#0A381F] mb-4">
                  <i className="ri-time-line text-[#D4AF37] mr-2"></i>
                  Better Work-Life Balance
                </h3>
                <p className="text-gray-700 mb-4">
                  Escape the NYC grind! Ireland offers 20+ days paid vacation (vs 10-15 in NYC), shorter commutes, and a culture that values life outside work. New Yorkers love the slower pace while still having access to a vibrant city life in Dublin.
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-check-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span>20-25 vacation days vs NYC's 10-15</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span>Shorter commutes (30 min vs 60+ in NYC)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span>Less stressful lifestyle overall</span>
                  </li>
                </ul>
              </div>
              <div className="border border-gray-200 rounded-lg p-6">
                <h3 className="text-2xl font-bold text-[#0A381F] mb-4">
                  <i className="ri-earth-line text-[#D4AF37] mr-2"></i>
                  Gateway to Europe
                </h3>
                <p className="text-gray-700 mb-4">
                  For New Yorkers who love to travel, Ireland is the perfect base. Weekend trips to Paris, London, Barcelona, or Rome cost $50-150 roundtrip. You'll explore more of Europe in one year than most Americans do in a lifetime.
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-check-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span>2-hour flights to most European cities</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span>Budget airlines (Ryanair) for $50 flights</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span>Easy weekend trips across Europe</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Cost Comparison */}
          <section className="mb-16">
            <h2 className="text-4xl font-bold text-[#0A381F] mb-8">NYC vs Dublin: Cost Comparison</h2>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-4 py-3 text-left text-[#0A381F]">Expense</th>
                    <th className="border border-gray-300 px-4 py-3 text-left text-[#0A381F]">New York City</th>
                    <th className="border border-gray-300 px-4 py-3 text-left text-[#0A381F]">Dublin, Ireland</th>
                    <th className="border border-gray-300 px-4 py-3 text-left text-[#0A381F]">Savings</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700">
                  <tr>
                    <td className="border border-gray-300 px-4 py-3 font-bold">1-Bed Apartment (Manhattan)</td>
                    <td className="border border-gray-300 px-4 py-3">$3,500-5,000</td>
                    <td className="border border-gray-300 px-4 py-3">$1,980-2,750</td>
                    <td className="border border-gray-300 px-4 py-3 text-green-600 font-bold">Save $1,500-2,250</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 px-4 py-3 font-bold">2-Bed Apartment</td>
                    <td className="border border-gray-300 px-4 py-3">$4,500-7,000</td>
                    <td className="border border-gray-300 px-4 py-3">$2,420-3,520</td>
                    <td className="border border-gray-300 px-4 py-3 text-green-600 font-bold">Save $2,000-3,500</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-3 font-bold">Monthly MetroCard/Transit</td>
                    <td className="border border-gray-300 px-4 py-3">$132</td>
                    <td className="border border-gray-300 px-4 py-3">$132-165</td>
                    <td className="border border-gray-300 px-4 py-3 text-gray-600">Similar</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 px-4 py-3 font-bold">Groceries (monthly)</td>
                    <td className="border border-gray-300 px-4 py-3">$500-700</td>
                    <td className="border border-gray-300 px-4 py-3">$400-600</td>
                    <td className="border border-gray-300 px-4 py-3 text-green-600 font-bold">Save $100-200</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-3 font-bold">Health Insurance</td>
                    <td className="border border-gray-300 px-4 py-3">$500-800/month</td>
                    <td className="border border-gray-300 px-4 py-3">$90-230/month</td>
                    <td className="border border-gray-300 px-4 py-3 text-green-600 font-bold">Save $400-600</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 px-4 py-3 font-bold">Dinner for 2 (mid-range)</td>
                    <td className="border border-gray-300 px-4 py-3">$80-120</td>
                    <td className="border border-gray-300 px-4 py-3">$66-99</td>
                    <td className="border border-gray-300 px-4 py-3 text-green-600 font-bold">Save $15-25</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-300 px-4 py-3 font-bold">Gym Membership</td>
                    <td className="border border-gray-300 px-4 py-3">$100-200</td>
                    <td className="border border-gray-300 px-4 py-3">$44-77</td>
                    <td className="border border-gray-300 px-4 py-3 text-green-600 font-bold">Save $50-125</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-300 px-4 py-3 font-bold">Internet + Mobile</td>
                    <td className="border border-gray-300 px-4 py-3">$120-180</td>
                    <td className="border border-gray-300 px-4 py-3">$77-110</td>
                    <td className="border border-gray-300 px-4 py-3 text-green-600 font-bold">Save $40-70</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="mt-6 bg-green-50 border-l-4 border-green-500 p-6">
              <p className="text-lg font-bold text-[#0A381F] mb-2">
                <i className="ri-money-dollar-circle-line text-green-600 mr-2"></i>
                Total Monthly Savings for New Yorkers:
              </p>
              <p className="text-2xl font-bold text-green-600">$2,000-4,000 per month</p>
              <p className="text-gray-700 mt-2">That's $24,000-48,000 per year you can save or invest by moving from NYC to Ireland!</p>
            </div>
          </section>

          {/* Jobs for New Yorkers */}
          <section className="mb-16">
            <h2 className="text-4xl font-bold text-[#0A381F] mb-8">Jobs in Ireland for New Yorkers</h2>
            <p className="text-lg text-gray-700 mb-8">
              New York professionals find excellent opportunities in Ireland, especially in tech, finance, and professional services. Many NYC companies have Dublin offices, making internal transfers common.
            </p>
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-2xl font-bold text-[#0A381F] mb-4">Tech Companies (NYC → Dublin)</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-building-line text-[#D4AF37] mr-2 mt-1"></i>
                    <div>
                      <strong>Google:</strong> Large Dublin office, easy transfers from NYC
                    </div>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-building-line text-[#D4AF37] mr-2 mt-1"></i>
                    <div>
                      <strong>Meta (Facebook):</strong> European HQ in Dublin
                    </div>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-building-line text-[#D4AF37] mr-2 mt-1"></i>
                    <div>
                      <strong>Apple:</strong> Major operations in Cork
                    </div>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-building-line text-[#D4AF37] mr-2 mt-1"></i>
                    <div>
                      <strong>Amazon:</strong> Growing Dublin presence
                    </div>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-building-line text-[#D4AF37] mr-2 mt-1"></i>
                    <div>
                      <strong>Microsoft:</strong> Dublin office accepts NYC transfers
                    </div>
                  </li>
                </ul>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-2xl font-bold text-[#0A381F] mb-4">Finance Companies (Wall St → Dublin)</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-bank-line text-[#D4AF37] mr-2 mt-1"></i>
                    <div>
                      <strong>Bank of America:</strong> Large Dublin operations
                    </div>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-bank-line text-[#D4AF37] mr-2 mt-1"></i>
                    <div>
                      <strong>Citibank:</strong> European operations in Ireland
                    </div>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-bank-line text-[#D4AF37] mr-2 mt-1"></i>
                    <div>
                      <strong>State Street:</strong> Major Irish presence
                    </div>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-bank-line text-[#D4AF37] mr-2 mt-1"></i>
                    <div>
                      <strong>JPMorgan:</strong> Dublin office for EU operations
                    </div>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-bank-line text-[#D4AF37] mr-2 mt-1"></i>
                    <div>
                      <strong>Goldman Sachs:</strong> Growing Dublin team
                    </div>
                  </li>
                </ul>
              </div>
            </div>
            <div className="bg-[#0A381F] text-white p-8 rounded-lg">
              <h3 className="text-2xl font-bold mb-4 text-[#D4AF37]">Salary Expectations for New Yorkers:</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <p className="font-bold mb-2">Software Engineer:</p>
                  <p>NYC: $120,000-180,000</p>
                  <p>Dublin: €70,000-110,000 ($77,000-121,000)</p>
                  <p className="text-sm text-[#D4AF37] mt-1">Similar purchasing power due to lower costs</p>
                </div>
                <div>
                  <p className="font-bold mb-2">Financial Analyst:</p>
                  <p>NYC: $80,000-120,000</p>
                  <p>Dublin: €55,000-85,000 ($60,500-93,500)</p>
                  <p className="text-sm text-[#D4AF37] mt-1">Better quality of life despite lower nominal salary</p>
                </div>
                <div>
                  <p className="font-bold mb-2">Product Manager:</p>
                  <p>NYC: $130,000-180,000</p>
                  <p>Dublin: €80,000-120,000 ($88,000-132,000)</p>
                  <p className="text-sm text-[#D4AF37] mt-1">Competitive with NYC after cost adjustments</p>
                </div>
                <div>
                  <p className="font-bold mb-2">Marketing Manager:</p>
                  <p>NYC: $90,000-130,000</p>
                  <p>Dublin: €60,000-90,000 ($66,000-99,000)</p>
                  <p className="text-sm text-[#D4AF37] mt-1">More savings potential in Ireland</p>
                </div>
              </div>
            </div>
          </section>

          {/* Lifestyle Comparison */}
          <section className="mb-16">
            <h2 className="text-4xl font-bold text-[#0A381F] mb-8">Lifestyle: NYC vs Dublin</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="border-2 border-gray-300 rounded-lg p-6">
                <h3 className="text-2xl font-bold text-[#0A381F] mb-4 text-center">New York City</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-subway-line text-gray-600 mr-2 mt-1"></i>
                    <span>24/7 subway, but crowded and unreliable</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-time-line text-gray-600 mr-2 mt-1"></i>
                    <span>Long commutes (60-90 minutes common)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-calendar-line text-gray-600 mr-2 mt-1"></i>
                    <span>10-15 vacation days per year</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-group-line text-gray-600 mr-2 mt-1"></i>
                    <span>Fast-paced, competitive culture</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-building-4-line text-gray-600 mr-2 mt-1"></i>
                    <span>Endless entertainment and dining options</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-cloudy-line text-gray-600 mr-2 mt-1"></i>
                    <span>Hot summers, cold winters</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-home-heart-line text-gray-600 mr-2 mt-1"></i>
                    <span>Small apartments, high density</span>
                  </li>
                </ul>
              </div>
              <div className="border-2 border-[#D4AF37] rounded-lg p-6">
                <h3 className="text-2xl font-bold text-[#0A381F] mb-4 text-center">Dublin, Ireland</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <i className="ri-bus-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span>Excellent public transport, less crowded</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-time-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span>Short commutes (20-30 minutes average)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-calendar-check-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span>20-25 vacation days per year</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-heart-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span>Relaxed, friendly culture</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-restaurant-2-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span>Great pubs, restaurants, and culture</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-temp-cold-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span>Mild year-round (no extreme weather)</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-home-smile-line text-[#D4AF37] mr-2 mt-1"></i>
                    <span>Larger apartments, more space</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Practical Tips */}
          <section className="mb-16">
            <h2 className="text-4xl font-bold text-[#0A381F] mb-8">Practical Tips for New Yorkers Moving to Ireland</h2>
            <div className="space-y-6">
              <div className="bg-yellow-50 border-l-4 border-[#D4AF37] p-6">
                <h3 className="text-xl font-bold text-[#0A381F] mb-3">
                  <i className="ri-flight-takeoff-line text-[#D4AF37] mr-2"></i>
                  Direct Flights from NYC
                </h3>
                <p className="text-gray-700 mb-3">
                  Multiple daily direct flights from JFK and Newark to Dublin (6-7 hours). Aer Lingus, United, and Delta all fly this route. Roundtrip tickets typically $400-800.
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Pro tip:</strong> Book flights 2-3 months in advance for best prices. Off-season (Nov-Mar) flights can be as low as $300 roundtrip.
                </p>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-6">
                <h3 className="text-xl font-bold text-[#0A381F] mb-3">
                  <i className="ri-bank-card-line text-blue-600 mr-2"></i>
                  Banking for New Yorkers
                </h3>
                <p className="text-gray-700 mb-3">
                  Keep your US bank account (Chase, Citi, BofA) and open an Irish account (AIB, Bank of Ireland). Use Wise or Revolut for currency transfers to avoid high fees.
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Pro tip:</strong> Many NYC banks charge foreign transaction fees. Get a no-fee credit card before moving.
                </p>
              </div>

              <div className="bg-green-50 border-l-4 border-green-500 p-6">
                <h3 className="text-xl font-bold text-[#0A381F] mb-3">
                  <i className="ri-phone-line text-green-600 mr-2"></i>
                  Your NYC Phone Number
                </h3>
                <p className="text-gray-700 mb-3">
                  Port your NYC number to Google Voice (free) to keep it for US banking and 2FA. Get an Irish SIM card (€20-40/month) for local calls and data.
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Pro tip:</strong> Irish carriers: Vodafone, Three, Eir. All offer unlimited data plans cheaper than Verizon/AT&T.
                </p>
              </div>

              <div className="bg-purple-50 border-l-4 border-purple-500 p-6">
                <h3 className="text-xl font-bold text-[#0A381F] mb-3">
                  <i className="ri-car-line text-purple-600 mr-2"></i>
                  Transportation Differences
                </h3>
                <p className="text-gray-700 mb-3">
                  Unlike NYC, you might not need a car in Dublin. Public transport is excellent and cycling is safe. If you do drive, remember: they drive on the LEFT side of the road!
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Pro tip:</strong> Your NY driver's license works for 12 months. After that, you'll need an Irish license (no test required for US license holders).
                </p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-500 p-6">
                <h3 className="text-xl font-bold text-[#0A381F] mb-3">
                  <i className="ri-shield-check-line text-red-600 mr-2"></i>
                  Healthcare Transition
                </h3>
                <p className="text-gray-700 mb-3">
                  Cancel your NYC health insurance once you're covered in Ireland. Register with a GP immediately (like finding a primary care doctor in NYC, but cheaper). Consider private insurance (€1,000-2,500/year vs $6,000-10,000 in NYC).
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Pro tip:</strong> Bring 3 months of prescriptions from NYC. You'll need to see an Irish GP to get new prescriptions.
                </p>
              </div>
            </div>
          </section>

          {/* New Yorker Community */}
          <section className="mb-16">
            <h2 className="text-4xl font-bold text-[#0A381F] mb-8">New York Expat Community in Ireland</h2>
            <div className="bg-[#0A381F] text-white p-8 rounded-lg">
              <p className="text-lg mb-6">
                You won't be alone! There's a thriving community of New Yorkers and Americans in Ireland. Many organize meetups, watch NFL games together, and help newcomers adjust.
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-xl font-bold text-[#D4AF37] mb-3">Where New Yorkers Gather:</h3>
                  <ul className="space-y-2">
                    <li>• American expat Facebook groups</li>
                    <li>• NYC alumni associations in Dublin</li>
                    <li>• Sports bars showing Yankees/Giants games</li>
                    <li>• Tech meetups (lots of ex-NYC techies)</li>
                    <li>• American Chamber of Commerce events</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#D4AF37] mb-3">Things You'll Miss (and Find!):</h3>
                  <ul className="space-y-2">
                    <li>• Bagels: Yes, Dublin has good bagel shops!</li>
                    <li>• Pizza: Not NYC-level, but decent options</li>
                    <li>• Delis: Irish supermarkets have most US brands</li>
                    <li>• Sports: Bars show NFL, NBA, MLB games</li>
                    <li>• Amazon: Amazon.ie delivers to Ireland</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-[#0A381F] to-[#0d4a28] text-white rounded-lg p-12 text-center">
            <h2 className="text-4xl font-bold mb-4">Ready to Leave NYC for Ireland?</h2>
            <p className="text-xl mb-8 opacity-90">
              Join thousands of New Yorkers who've made the move. Get personalized guidance for your relocation from the Big Apple to the Emerald Isle.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/directory" 
                className="bg-[#D4AF37] text-[#0A381F] px-10 py-4 rounded-lg font-bold text-lg hover:bg-[#c49d2f] transition-colors whitespace-nowrap"
              >
                Find Relocation Services
              </Link>
              <Link 
                to="/tools/cost-calculator" 
                className="bg-white text-[#0A381F] px-10 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
              >
                Calculate Your Costs
              </Link>
              <button 
                onClick={() => document.querySelector('#vapi-widget-floating-button')?.click()}
                className="bg-transparent border-2 border-white text-white px-10 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-[#0A381F] transition-colors whitespace-nowrap"
              >
                Ask Our AI Assistant
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}